import { Controller } from "@hotwired/stimulus"

/**
 * Wraps the AI importer turbo frame to provide smooth height transitions
 * when content is replaced (form → processing state → success).
 * Lives OUTSIDE the turbo frame so it persists across replacements.
 */
export default class extends Controller {
  private observer: MutationObserver | null = null
  private currentHeight: number = 0

  connect(): void {
    this.currentHeight = this.element.clientHeight
    this.observeFrameChanges()
  }

  disconnect(): void {
    this.observer?.disconnect()
  }

  private observeFrameChanges(): void {
    this.observer = new MutationObserver(() => {
      this.animateHeightTransition()
    })

    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: false
    })
  }

  private animateHeightTransition(): void {
    const wrapper = this.element as HTMLElement
    const previousHeight = this.currentHeight
    const newHeight = wrapper.scrollHeight

    if (previousHeight > 0 && Math.abs(newHeight - previousHeight) > 20) {
      // Lock to previous height, then transition to new height
      wrapper.style.height = `${previousHeight}px`
      wrapper.style.overflow = "hidden"
      wrapper.style.transition = "height 0.5s ease"

      requestAnimationFrame(() => {
        wrapper.style.height = `${newHeight}px`

        const onEnd = () => {
          wrapper.style.height = ""
          wrapper.style.overflow = ""
          wrapper.style.transition = ""
          wrapper.removeEventListener("transitionend", onEnd)
        }
        wrapper.addEventListener("transitionend", onEnd, { once: true })

        // Fallback cleanup in case transitionend doesn't fire
        setTimeout(onEnd, 600)
      })
    }

    this.currentHeight = newHeight
  }
}
